import React, { useState, useMemo } from 'react';
import diningData from '../data/diningData.json';
import { usePS2Graphics } from '../context/PS2GraphicsContext';
import { useToast } from './Toast';
import { GolfFlagIcon, DiningPlateIcon, CountryClubCrest, CheckIcon } from './Icons';

export default function ClubhouseDining() {
  const { ps2Mode, ps2CardView, playPs2Click, playPs2Select, playPs2Order } = usePS2Graphics();
  const toast = useToast();

  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [tabCart, setTabCart] = useState([]);
  const [isTabOpen, setIsTabOpen] = useState(false);
  const [golferName, setGolferName] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('Hole 18 Fairway');
  const [specialNotes, setSpecialNotes] = useState('');
  const [receiptOrder, setReceiptOrder] = useState(null);

  const categories = ['All', 'Burgers & Dogs', 'Fairway Bites', '19th Hole Drinks', 'PS2 Retro Sweets'];

  // Filtered menu items
  const filteredItems = useMemo(() => {
    return diningData.filter(item => {
      const matchCat = category === 'All' || item.category === category;
      const matchSearch = search === '' ||
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [category, search]);

  // Tab cart operations
  const addToTab = (item) => {
    playPs2Click();
    setTabCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { ...item, qty: 1 }];
    });
    toast.info(`Added ${item.name} to tab`);
  };

  const updateQty = (id, delta) => {
    playPs2Click();
    setTabCart(prev => {
      return prev.map(i => {
        if (i.id === id) {
          const newQty = i.qty + delta;
          return newQty > 0 ? { ...i, qty: newQty } : null;
        }
        return i;
      }).filter(Boolean);
    });
  };

  const totalTab = tabCart.reduce((sum, item) => sum + item.price * item.qty, 0);

  // Submit Order
  const handlePlaceOrder = (e) => {
    e.preventDefault();
    if (tabCart.length === 0) return;
    if (!golferName.trim()) {
      toast.warn('Please enter your Golfer / Member Name');
      return;
    }

    playPs2Order();
    const orderData = {
      orderId: 'CC-' + Math.floor(100000 + Math.random() * 900000),
      golferName,
      deliveryLocation,
      specialNotes,
      items: tabCart,
      total: totalTab + (totalTab * 0.08), // tax
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    // Post order to backend if running
    fetch('/api/dining/order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData),
    }).catch(() => {});

    setReceiptOrder(orderData);
    setTabCart([]);
    setIsTabOpen(false);
    toast.success(`Order ${orderData.orderId} sent to Clubhouse Kitchen!`);
  };

  return (
    <section id="dining-section" className="relative px-4 md:px-6 py-12 max-w-6xl mx-auto">
      {/* Header Banner */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-mono tracking-widest uppercase mb-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
          <GolfFlagIcon className="w-3.5 h-3.5 text-emerald-400" />
          <span>19th HOLE CLUBHOUSE GRILL & BAR</span>
        </div>
        <h1 className="text-3xl md:text-5xl font-black text-white font-mono tracking-tight mb-3">
          Country Club Dining
        </h1>
        <p className="text-white/60 font-mono text-sm max-w-xl mx-auto">
          Fresh golf course smashburgers, fairway snacks, craft cocktails, and retro PS2 treats. Delivered to your tee box or clubhouse table!
        </p>
      </div>

      {/* Filter & Search Toolbar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-2 sm:pb-0 scrollbar-none">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => { setCategory(cat); playPs2Click(); }}
              className={`px-3 py-1.5 rounded-xl text-xs font-mono whitespace-nowrap transition-all ${
                category === cat
                  ? ps2Mode
                    ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-400/50 shadow-[0_0_12px_rgba(6,182,212,0.25)] font-bold'
                    : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold'
                  : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10 border border-white/10'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Search & Tab Button */}
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search menu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1.5 rounded-xl text-xs font-mono bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-emerald-500/40 w-full sm:w-48"
          />

          <button
            onClick={() => { setIsTabOpen(prev => !prev); playPs2Select(); }}
            className="flex items-center gap-2 px-4 py-1.5 rounded-xl font-mono text-xs font-bold text-white transition-all shadow-lg whitespace-nowrap cursor-pointer"
            style={{
              background: ps2Mode
                ? 'linear-gradient(135deg, rgba(6,182,212,0.4), rgba(59,130,246,0.3))'
                : 'linear-gradient(135deg, #16a34a, #15803d)',
            }}
          >
            <DiningPlateIcon className="w-4 h-4 text-white" />
            <span>Tab</span>
            {tabCart.length > 0 && (
              <span className="w-5 h-5 rounded-full bg-white text-black text-[10px] font-black flex items-center justify-center">
                {tabCart.reduce((a, b) => a + b.qty, 0)}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Menu Items Grid */}
      <div
        className={`grid gap-5 ${
          ps2CardView
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-1 md:grid-cols-2'
        }`}
      >
        {filteredItems.map(item => (
          <div
            key={item.id}
            className={`group p-5 rounded-2xl border transition-all duration-300 flex flex-col justify-between relative overflow-hidden ${
              ps2Mode || ps2CardView
                ? 'bg-slate-900/80 border-cyan-500/30 hover:border-cyan-400 hover:shadow-[0_0_20px_rgba(6,182,212,0.2)]'
                : 'bg-white/[0.02] border-white/10 hover:border-emerald-500/30 hover:bg-white/[0.04]'
            }`}
          >
            {/* Top row badge */}
            <div>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl p-2 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    {item.emoji}
                  </span>
                  <div>
                    <span className="text-[10px] font-mono text-emerald-400 uppercase tracking-widest block font-bold">
                      {item.badge}
                    </span>
                    <h3 className="text-base font-bold text-white font-mono leading-tight group-hover:text-emerald-300 transition-colors">
                      {item.name}
                    </h3>
                  </div>
                </div>
                <span className="text-base font-bold font-mono text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-xl border border-emerald-500/20 whitespace-nowrap">
                  ${item.price.toFixed(2)}
                </span>
              </div>

              <p className="text-xs text-white/60 font-mono leading-relaxed mb-4">
                {item.description}
              </p>
            </div>

            {/* Bottom row: Calories & PS2 Memory Card File Tag */}
            <div className="pt-3 border-t border-white/10 flex items-center justify-between font-mono text-xs">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-white/40">{item.calories}</span>
                {ps2Mode && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-cyan-300 border border-cyan-500/30">
                    {item.ps2Block}
                  </span>
                )}
              </div>

              <button
                onClick={() => addToTab(item)}
                className="px-3 py-1.5 rounded-xl font-mono text-xs font-bold text-white bg-white/10 hover:bg-emerald-500/20 hover:text-emerald-300 hover:border-emerald-500/40 border border-white/15 transition-all"
              >
                + Add to Tab
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Slide-over Clubhouse Tab Drawer */}
      {isTabOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/70 backdrop-blur-sm animate-boot-1">
          <div className="w-full max-w-md bg-zinc-900 border-l border-white/15 p-6 flex flex-col justify-between h-full overflow-y-auto">
            <div>
              <div className="flex items-center justify-between pb-4 border-b border-white/10 mb-5">
                <div className="flex items-center gap-2">
                  <span className="text-xl">⛳</span>
                  <h2 className="text-lg font-mono font-bold text-white">Your Clubhouse Tab</h2>
                </div>
                <button
                  onClick={() => setIsTabOpen(false)}
                  className="w-8 h-8 rounded-full bg-white/10 text-white font-mono flex items-center justify-center hover:bg-white/20"
                >
                  ✕
                </button>
              </div>

              {tabCart.length === 0 ? (
                <div className="text-center py-16 font-mono text-white/40">
                  <p className="text-3xl mb-2">🌭</p>
                  <p>Your tab is empty</p>
                  <p className="text-xs text-white/30 mt-1">Add burgers, drinks or treats to order!</p>
                </div>
              ) : (
                <div className="space-y-3 mb-6">
                  {tabCart.map(item => (
                    <div key={item.id} className="p-3 rounded-xl bg-white/5 border border-white/10 flex items-center justify-between font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <span>{item.emoji}</span>
                        <div>
                          <div className="font-bold text-white">{item.name}</div>
                          <div className="text-white/40 text-[10px]">${item.price.toFixed(2)} each</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQty(item.id, -1)}
                          className="w-6 h-6 rounded bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold"
                        >
                          -
                        </button>
                        <span className="font-bold text-emerald-400">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.id, 1)}
                          className="w-6 h-6 rounded bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {tabCart.length > 0 && (
              <form onSubmit={handlePlaceOrder} className="pt-4 border-t border-white/10 space-y-3 font-mono text-xs">
                <div>
                  <label className="block text-white/60 mb-1 text-[10px] uppercase">Golfer / Member Name</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Tiger Woods"
                    value={golferName}
                    onChange={(e) => setGolferName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-emerald-400"
                  />
                </div>

                <div>
                  <label className="block text-white/60 mb-1 text-[10px] uppercase">Delivery Location</label>
                  <select
                    value={deliveryLocation}
                    onChange={(e) => setDeliveryLocation(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-emerald-400"
                  >
                    <option value="Hole 1 Tee Box">Hole 1 Tee Box</option>
                    <option value="Hole 9 Snack Shack">Hole 9 Snack Shack</option>
                    <option value="Hole 18 Fairway">Hole 18 Fairway</option>
                    <option value="PS2 Memory Card Lounge">PS2 Memory Card Lounge</option>
                    <option value="Clubhouse Main Bar">Clubhouse Main Bar</option>
                    <option value="Cabana 4 Poolside">Cabana 4 Poolside</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white/60 mb-1 text-[10px] uppercase">Special Notes</label>
                  <input
                    type="text"
                    placeholder="Extra sauce, no onions, etc."
                    value={specialNotes}
                    onChange={(e) => setSpecialNotes(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-emerald-400"
                  />
                </div>

                <div className="pt-2 flex items-center justify-between text-sm font-bold border-t border-white/10">
                  <span className="text-white">Total (inc. tax):</span>
                  <span className="text-emerald-400 text-base">${(totalTab * 1.08).toFixed(2)}</span>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 rounded-xl font-mono text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-xl transition-all"
                >
                  ⛳ Send Order to Kitchen
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Retro PS2 Thermal Receipt Modal */}
      {receiptOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-boot-1">
          <div className="bg-amber-50 text-zinc-900 font-mono p-6 rounded-xl max-w-sm w-full shadow-2xl border border-amber-200">
            <div className="text-center border-b border-dashed border-zinc-400 pb-3 mb-3">
              <h3 className="font-black text-base uppercase tracking-widest text-zinc-900">
                ⛳ THE COUNTRY CLUB ⛳
              </h3>
              <p className="text-[10px] text-zinc-600">19th HOLE KITCHEN & SNACK BAR</p>
              <p className="text-[10px] text-zinc-500 mt-1">RECEIPT: #{receiptOrder.orderId}</p>
              <p className="text-[10px] text-zinc-500">{receiptOrder.timestamp}</p>
            </div>

            <div className="text-xs space-y-1 mb-3">
              <div><span className="font-bold">MEMBER:</span> {receiptOrder.golferName}</div>
              <div><span className="font-bold">LOCATION:</span> {receiptOrder.deliveryLocation}</div>
              {receiptOrder.specialNotes && (
                <div><span className="font-bold">NOTES:</span> {receiptOrder.specialNotes}</div>
              )}
            </div>

            <div className="border-t border-b border-dashed border-zinc-400 py-2 my-2 space-y-1.5 text-xs">
              {receiptOrder.items.map(item => (
                <div key={item.id} className="flex justify-between">
                  <span>{item.qty}x {item.name}</span>
                  <span className="font-bold">${(item.price * item.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="text-right text-sm font-black text-zinc-900 mb-4">
              TOTAL: ${receiptOrder.total.toFixed(2)}
            </div>

            <div className="text-center text-[10px] text-zinc-500 mb-4">
              *** THANK YOU FOR VISITING THE COUNTRY CLUB ***<br />
              [128-BIT PS2 MEMORY CARD SAVE VERIFIED]
            </div>

            <button
              onClick={() => setReceiptOrder(null)}
              className="w-full py-2 bg-zinc-900 text-amber-50 font-bold rounded-lg hover:bg-zinc-800 text-xs transition-colors"
            >
              Close Receipt
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
